/**
 * **Mapa de rutas** del aplicativo privado DeclaraBID: define los *shells* por rol y delega en
 * `loadComponent` / `loadChildren` para cada feature documentada con `@module` en su archivo ancla.
 *
 * ## Autenticación
 *
 * - `auth/login` — solo invitados (`isNotAuthGuard`). Dominio **Auth** (`auth.service.ts`, interceptor).
 *
 * ## Administrador (`/admin`)
 *
 * Componente shell: {@link AdminComponent}. Requiere `isAuthGuard` + `isAdminGuard`.
 * Incluye entre otros: rutas `home` / `dashboard`, **PublicWebsite**, **Institutions**,
 * **TemplatesList** / **TemplatesCreator** / **TemplatesEditor**, **StatementsAdministration** /
 * **StatementFormSection**, **StatementAssignment** (lazy), **Report** (lazy),
 * **ConfigSystem** (lazy), **Users**.
 *
 * ## Declarante (`/declarant`)
 *
 * Shell {@link DeclarantComponent}; `isAuthGuard` + `isDeclarantGuard`. **DashboardDeclarant**,
 * **DeclarantSection** (formulario por sección; `canAccessDeclarantStartDate` donde aplica),
 * **MyDeclarations**.
 *
 * ## Revisor (`/reviewer`)
 *
 * Shell {@link ReviewerComponent}; **DashboardReviewer**, **Users**, **Reviews**, **ReviewsForms**
 * (detalle por formulario y sección), **StatementAssignment** (lazy, mismo archivo de rutas que en admin).
 *
 * ## RR.HH. (`/rh`)
 *
 * Shell {@link RhComponent}; **DashboardRh**, **Users** (parcial), **Report** (lazy, mismo módulo que admin).
 *
 * @packageDocumentation
 */

import { Routes } from '@angular/router';
import { AdminComponent } from './features/admin/components/admin/admin.component';
import { isAdminGuard } from './core/guards/is-admin.guard';
import { isAuthGuard } from './core/guards/is-auth.guard';
import { isNotAuthGuard } from './core/guards/is-not-auth.guard';
import { DeclarantComponent } from './features/declarant/components/declarant/declarant.component';
import { ReviewerComponent } from './features/reviewer/components/reviewer/reviewer.component';
import { RhComponent } from './features/rh/components/rh/rh.component';
import { isDeclarantGuard } from './core/guards/is-declarant.guard';
import { isReviewerGuard } from './core/guards/is-reviewer.guard';
import { isRhGuard } from './core/guards/is-rh.guard';
import { PageNotFoundComponent } from './shared/components/page-not-found/page-not-found.component';
import { canAccessDeclarantStartDate } from './core/guards/can-access-declarant-start-date.guard';

export const mainAppRoutes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./shared/components/app-bootstrap/app-bootstrap.component').then(
        (c) => c.AppBootstrapComponent,
      ),
  },
  {
    path: 'auth',
    canActivate: [isNotAuthGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/pages/login/login.component').then(
            (c) => c.LoginComponent,
          ),
      },
    ],
  },
  {
    path: 'verify-code/:id/:code',
    loadComponent: () =>
      import(
        './features/verify-code/pages/verify-code/verify-code.component'
      ).then((c) => c.VerifyCodeComponent),
  },
  {
    path: 'admin',
    canActivate: [isAuthGuard, isAdminGuard],
    component: AdminComponent,
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('./features/home/pages/home-admin/home-admin.component').then(
            (c) => c.HomeAdminComponent,
          ),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/home/pages/home/home.component').then(
            (c) => c.HomeComponent,
          ),
      },
      {
        path: 'public-website',
        loadComponent: () =>
          import(
            './features/public-website/page/public-website/public-website.component'
          ).then((c) => c.PublicWebsiteComponent),
      },
      {
        path: 'institutions',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/institutions/pages/institutions/institutions.component'
              ).then((c) => c.InstitutionsComponent),
          },
          {
            path: 'add-institutions',
            loadComponent: () =>
              import(
                './features/institutions/pages/add-institutions/add-institutions.component'
              ).then((c) => c.AddInstitutionsComponent),
          },
          {
            path: 'edit-institutions',
            loadComponent: () =>
              import(
                './features/institutions/pages/edit-institutions/edit-institutions.component'
              ).then((c) => c.EditInstitutionsComponent),
          },
        ],
      },
      {
        path: 'templates',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/templates/pages/templates/templates.component'
              ).then((c) => c.TemplatesComponent),
          },
          {
            path: 'template-creator',
            loadComponent: () =>
              import(
                './features/templates/pages/template-creator/template-creator.component'
              ).then((c) => c.TemplateCreatorComponent),
          },
          {
            path: 'template-editor',
            loadComponent: () =>
              import(
                './features/templates/pages/template-editor/template-editor.component'
              ).then((c) => c.TemplateEditorComponent),
          },
        ],
      },
      {
        path: 'report',
        loadChildren: () => import('./features/report/pages/report.routes'),
      },
      {
        path: 'statement-assignment',
        loadChildren: () =>
          import('./features/statement-assignment/pages/statement.routes'),
      },
      {
        path: 'manage-statements',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/manage-statements/pages/manage-statements/manage-statements.component'
              ).then((c) => c.ManageStatementsComponent),
            children: [],
          },
          {
            path: 'form-section/:id/:user_id',
            loadComponent: () =>
              import(
                './features/manage-statements/components/form-section/form-section.component'
              ).then((c) => c.FormSectionComponent),
          },
          {
            path: 'form-section/:id/:user_id/:section',
            loadComponent: () =>
              import(
                './features/manage-statements/components/form-section/form-section.component'
              ).then((c) => c.FormSectionComponent),
          },
        ],
      },
      {
        path: 'config-system',
        loadChildren: () =>
          import('./features/config-system/page/config-system.routes'),
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/users/pages/users/users.component').then(
                (c) => c.UsersComponent,
              ),
          },
          {
            path: 'add-users',
            loadComponent: () =>
              import(
                './features/users/pages/add-users/add-users.component'
              ).then((c) => c.AddUsersComponent),
          },
          {
            path: 'edit-user',
            loadComponent: () =>
              import(
                './features/users/pages/edit-users/edit-users.component'
              ).then((c) => c.EditUsersComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'declarant',
    canActivate: [isAuthGuard, isDeclarantGuard],
    component: DeclarantComponent,
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import(
            './features/dashboard-declarant/pages/dashboard-declarant/dashboard-declarant.component'
          ).then((c) => c.DashboardDeclarantComponent),
      },
      {
        path: 'declarant-section/:id',
        canActivate: [canAccessDeclarantStartDate],
        loadComponent: () =>
          import(
            './features/dashboard-declarant/components/declarant-section/declarant-section.component'
          ).then((c) => c.DeclarantSectionComponent),
      },
      {
        path: 'declarant-section/:id/report',
        loadComponent: () =>
          import(
            './features/dashboard-declarant/components/declarant-report/declarant-report.component'
          ).then((c) => c.DeclarantReportComponent),
      },
      {
        path: 'my-declarations',
        loadComponent: () =>
          import(
            './features/my-declarations/pages/my-declarations/my-declarations.component'
          ).then((c) => c.MyDeclarationsComponent),
      },
      {
        path: 'declarant-section/:id/:user_id',
        canActivate: [canAccessDeclarantStartDate],
        loadComponent: () =>
          import(
            './features/dashboard-declarant/components/declarant-section/declarant-section.component'
          ).then((c) => c.DeclarantSectionComponent),
      },
    ],
  },
  {
    path: 'reviewer',
    canActivate: [isAuthGuard, isReviewerGuard],
    component: ReviewerComponent,
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import(
            './features/home/pages/home-reviewer/home-reviewer.component'
          ).then((c) => c.HomeReviewerComponent),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import(
            './features/dashboard-reviewer/pages/dashboard-reviewer/dashboard-reviewer.component'
          ).then((c) => c.DashboardReviewerComponent),
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/users/pages/users/users.component').then(
                (c) => c.UsersComponent,
              ),
          },
          {
            path: 'add-users',
            loadComponent: () =>
              import(
                './features/users/pages/add-users/add-users.component'
              ).then((c) => c.AddUsersComponent),
          },
          {
            path: 'edit-user',
            loadComponent: () =>
              import(
                './features/users/pages/edit-users/edit-users.component'
              ).then((c) => c.EditUsersComponent),
          },
        ],
      },
      {
        path: 'reviews',
        loadComponent: () =>
          import('./features/reviews/pages/reviews/reviews.component').then(
            (c) => c.ReviewsComponent,
          ),
      },
      {
        path: 'reviews-forms/:id/:user_id/:section',
        loadComponent: () =>
          import(
            './features/reviews/pages/reviews-forms/reviews-forms.component'
          ).then((m) => m.ReviewsFormsComponent),
      },
      {
        path: 'statement-assignment',
        loadChildren: () =>
          import('./features/statement-assignment/pages/statement.routes'),
      },
      {
        path: 'report',
        loadChildren: () =>
          import('./features/report/pages/report.routes'),
      },
    ],
  },
  {
    path: 'rh',
    canActivate: [isAuthGuard, isRhGuard],
    component: RhComponent,
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import(
            './features/dashboard-rh/pages/dashboard-rh/dashboard-rh.component'
          ).then((c) => c.DashboardRhComponent),
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/users/pages/users/users.component').then(
                (c) => c.UsersComponent,
              ),
          },
          // {
          //   path: 'add-users',
          //   loadComponent: () =>
          //     import(
          //       './features/users/pages/add-users/add-users.component'
          //     ).then((c) => c.AddUsersComponent),
          // },
          {
            path: 'edit-user',
            loadComponent: () =>
              import(
                './features/users/pages/edit-users/edit-users.component'
              ).then((c) => c.EditUsersComponent),
          },
        ],
      },
      {
        path: 'report',
        loadChildren: () => import('./features/report/pages/report.routes'),
      },
    ],
  },
  {
    path: '**',
    component: PageNotFoundComponent,
  },
];
